import { IDropdownOption, Ratings, Toast, ToastModel } from '@notino/react-styleguide'
import dayjs, { Dayjs } from 'dayjs'
import { OverlayScrollbarsComponentProps } from 'overlayscrollbars-react'
import { FormatDateOptions, IntlFormatters, IntlShape } from 'react-intl'
import qs from 'query-string'
import * as Sentry from '@sentry/react'
import React from 'react'
import axios, { AxiosError } from 'axios'
import { IToastProps } from '@notino/react-styleguide/dist/types/components/atoms/Toast'
import { toast, ToastContentProps } from 'react-toastify'

// types
import {
	Phone,
	Price,
	SalonServicesResponse,
	ConfigResponse,
	Cosmetic,
	Language,
	GetSalonsFilterCountResponse,
	AvailableRsTimeSlotsResponse,
	SalonsCategoryPageContainerQuery,
	ServicesTopCategoryData,
	ServiceCategoryData,
	SalonPageQuery,
	PartialRatingType,
	SalonsListPageContainerQuery,
	ReservationBookingModalInitData,
	HomePageContainerQuery,
	ShopId,
	CommonSalonsPageContainerQuery,
	CustomConfig,
	BasicAxiosError,
	CurrencyCode,
	AvailableLanguages,
	CountryCode
} from '../types/types'
import { CheckboxGroupOption } from '../formFields/CheckboxGroupFormField/types'

// enums
import {
	DAY_NAME_DATE_FORMAT,
	RESERVATIONS_TIME_SLOTS,
	SALONS_FILTER_TYPE,
	SORTING_OPTION,
	DAY_NAME_DATE_YEAR_FORMAT,
	RATINGS,
	PARTIAL_RATING,
	GOOGLE_MAPS_URL,
	MSG_TYPE,
	ErrorMessage,
	RATING_ID,
	PRODUCTION_ENVIRONMENT
} from './enums'

// pats
import Paths from '../routes/paths'

// utils
import { getIntl } from './intl'
import { AVAILABLE_LANGUAGES, DEFAULT_LOCALE, getEnvironmentAndLocaleSpecificShopsConfig } from '../appDefaults'

const getCountryByCode = (countries: ConfigResponse['rolloutCountries'], code: string): ConfigResponse['rolloutCountries'][0] | undefined => {
	return countries.find((country) => country.code === code)
}

/**
 * Finds the corresponding value in an errors map for a given error key.
 * @template T - The type of the errors map.
 * @param errorKey - The key from the error message, potentially with indices.
 * @param errorsMap - The mapping of error paths to form fields.
 * @returns The mapped form field or `undefined` if no match is found.
 */
export const findErrorKeyValue = <T extends Record<string, any>>(errorKey: string, errorsMap: T): T[keyof T] | undefined => {
	// Return the correct type based on the map's value type
	// Transform the errorKey to replace numbers with '[number]'
	const transformedKey = errorKey.replace(/\[\d+\]/g, '[number]')

	// Return the value or undefined if not found
	return errorsMap[transformedKey]
}

export const ERROR_MESSAGES = () => {
	const intl = getIntl()

	return {
		GENERAL_ERROR: intl.formatMessage({
			id: 'Something went wrong. Please try again or contact our support.',
			defaultMessage: 'Something went wrong. Please try again or contact our support.'
		}),
		UNAUTHORIZED_MSG: intl.formatMessage({
			id: 'Unauthorized. You must be logged in to perform this action.',
			defaultMessage: 'Unauthorized. You must be logged in to perform this action.'
		}),
		GENERAL_FORM_ERROR_MSG: intl.formatMessage({
			id: 'Something went wrong. Please check the data in the form or try again.',
			defaultMessage: 'Something went wrong. Please check the data in the form or try again.'
		}),
		SERVER_ERROR: intl.formatMessage({
			id: 'Server connection error.',
			defaultMessage: 'Server connection error.'
		})
	}
}

/**
 * Get phone number
 * @param phone IPhone
 * @returns {string}
 */
export const getPhoneNumber = (phone?: Phone, countries?: ConfigResponse['rolloutCountries']): string => {
	if (!phone || !countries || countries?.length < 0) {
		return ''
	}
	const country = getCountryByCode(countries, phone.phonePrefixCountryCode)
	const result = `${country?.phonePrefix}${phone.phone}`
	return result
}

/**
 * Get first level categories
 * @param {categories} ISalonServicesResponse
 * @returns {string[]}
 */
export const getFirstLevelCategoriesNames = (categories?: SalonServicesResponse): string[] => {
	if (!categories) {
		return []
	}
	const result: string[] = []
	categories?.groupedServicesByCategory?.forEach((obj) => obj.category?.name && result.push(obj.category?.name))
	return result
}

export const repeat = (textToRepeat: string, numberOfTimesToRepeat: number): string => {
	let result = ''
	for (let i = 0; i < numberOfTimesToRepeat; i += 1) {
		result = `${result}${textToRepeat}`
	}
	return result
}

/**
 * Transforms normalized form to number
 * @param {Price | null} [price]
 * @returns {number}
 */
export const decodePrice = (price: Price | null | undefined): number | null | undefined => {
	if (!price) {
		return null
	}

	const { exponent, significand } = price

	// Calculate the decimal places from the exponent
	const decimalPlaces = Math.abs(exponent)

	// Calculate the result
	const result = significand * 10 ** exponent

	// Use toFixed() to format the number with the calculated decimal places
	const formattedResult = result.toFixed(decimalPlaces)

	// Convert the formatted result back to a number
	const numberResult = parseFloat(formattedResult)

	return numberResult
}

/**
 * Check if is pdf file or not
 * @param {fileUrl | null | undefined} [string]
 * @returns {boolean}
 */
export const isFilePDF = (fileUrl?: string): boolean => {
	if (!fileUrl) {
		return false
	}

	return !!fileUrl.endsWith('.pdf')
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const normalizeQueryParams = (queryParams: any) =>
	Object.keys(queryParams)?.map((queryParam) => {
		if (queryParam === '' || queryParam === undefined || queryParam === null) {
			return undefined
		}
		return queryParam
	})

export const getCategoryIDs = (subCategoriesIDs: string[], topLevelCategoryID: string | undefined) => {
	let categoryIDs: string[] | undefined
	if (subCategoriesIDs && subCategoriesIDs.length > 0) {
		categoryIDs = subCategoriesIDs
	} else if (topLevelCategoryID) {
		categoryIDs = [topLevelCategoryID]
	}
	return categoryIDs
}

export const getValidServiceCategories = (categoriesData: SalonServicesResponse['groupedServicesByCategory']): ServicesTopCategoryData[] => {
	return categoriesData.flatMap((industry) => (industry.category ? [industry.category] : []))
}

export const isElementOverflownX = ({ clientWidth, scrollWidth }: { clientWidth: number; scrollWidth: number }) => {
	return scrollWidth > clientWidth
}

export const toHoursAndMinutes = (totalMinutes?: number) => {
	if (!totalMinutes) {
		return ''
	}
	const hours = Math.floor(totalMinutes / 60)
	const minutes = totalMinutes % 60
	return `${hours > 0 ? `${hours}h` : ''}${minutes > 0 ? ` ${minutes}m` : ''}`
}

/** Spoken duration for screen readers (e.g. "1 hour 44 minutes"), not compact "1h 44m". */
export const formatAccessibleDuration = (totalMinutes?: number) => {
	if (!totalMinutes) {
		return ''
	}

	const hours = Math.floor(totalMinutes / 60)
	const minutes = totalMinutes % 60
	const { formatMessage } = getIntl()

	return formatMessage(
		{
			id: '{hours, plural, =0 {} one {# hour} other {# hours}}{minutes, plural, =0 {} one { # minute} other { # minutes}}',
			defaultMessage: '{hours, plural, =0 {} one {# hour} other {# hours}}{minutes, plural, =0 {} one { # minute} other { # minutes}}'
		},
		{ hours, minutes }
	).trim()
}

export const getGoogleMapsLink = ({ lat, lon }: { lat: number | undefined; lon: number | undefined }) => {
	return lat && lon ? `${GOOGLE_MAPS_URL}/?q=${lat},${lon}` : undefined
}

export const formatNumberValueOnChange = (value: string) => {
	return value.replace(/[^0-9,.]/g, '')
}

export const formatNumber = (value?: string | number) => {
	if (Number.isNaN(value)) return '' // Handle empty input

	let formattedValue = String(value)

	// Remove leading zero numbers, except when it's just a single zero
	if (formattedValue !== '0' && !/^0[.,]/.test(formattedValue)) {
		formattedValue = formattedValue.replace(/^0+/, '')
	}

	// Remove any non-numeric characters
	formattedValue = formattedValue.replace(/[^0-9,.]/g, '')

	// Replace periods with commas for the decimal separator
	formattedValue = formattedValue.replace(/\./g, ',')

	// Split the value into integer and decimal parts
	const [integerPart, decimalPart] = formattedValue.split(',')

	// Add thousands separators to the integer part
	const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

	// Ensure the decimal part has at most two decimal places
	const formattedDecimalPart = decimalPart ? `,${decimalPart.slice(0, 2)}` : ''

	return formattedIntegerPart + formattedDecimalPart
}

export const formatRating = (intl: IntlShape, rating: number) => intl.formatNumber(rating, { minimumFractionDigits: 1, maximumFractionDigits: 1 })

const getPriceFormat = (locale: string, currency: string, value: number) => {
	const isInteger = Number.isInteger(value)
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		minimumFractionDigits: isInteger ? 0 : 2,
		maximumFractionDigits: 2
	})
}

type FormatPriceArgs = {
	from?: Price | number | null
	to?: Price | number | null
	fromSecondary?: Price | number | null
	toSecondary?: Price | number | null
	currency?: string
	currencySecondary?: string
}

export const formatPrice = (args: FormatPriceArgs): string | null => {
	const { from, to, fromSecondary, toSecondary, currency: currencyArgs, currencySecondary: currencySecondaryArgs } = args
	const { locale } = getIntl()

	const decodedPriceFrom = typeof from === 'number' ? from : decodePrice(from)
	const decodedPriceTo = typeof to === 'number' ? to : decodePrice(to)
	const decodedPriceFromSecondary = typeof fromSecondary === 'number' ? fromSecondary : decodePrice(fromSecondary)
	const decodedPriceToSecondary = typeof toSecondary === 'number' ? toSecondary : decodePrice(toSecondary)

	const fromExists = typeof decodedPriceFrom === 'number'
	const toExists = typeof decodedPriceTo === 'number'
	const fromSecondaryExists = typeof decodedPriceFromSecondary === 'number'
	const toSecondaryExists = typeof decodedPriceToSecondary === 'number'
	const currency = currencyArgs || (typeof from === 'object' && from?.currency) || (typeof to === 'object' && to?.currency) || undefined
	const currencySecondary =
		currencySecondaryArgs ||
		(typeof fromSecondary === 'object' && fromSecondary?.currency) ||
		(typeof toSecondary === 'object' && toSecondary?.currency) ||
		undefined

	if (
		(!fromExists && !toExists && !fromSecondaryExists && !toSecondaryExists) ||
		((fromExists || toExists) && !currency) ||
		((fromSecondaryExists || toSecondaryExists) && !currencySecondary)
	) {
		return null
	}

	const formatValue = (value: number, curr: string) => getPriceFormat(locale, curr, value).format(value)

	const formatRange = (fromVal: number | null | undefined, toVal: number | null | undefined, curr: string) => {
		if (typeof fromVal === 'number' && typeof toVal === 'number') {
			if (fromVal === toVal) {
				return formatValue(fromVal, curr)
			}

			const numberFormat = getPriceFormat(locale, curr, fromVal)

			if (typeof numberFormat.formatRange === 'function') {
				return numberFormat.formatRange(fromVal, toVal)
			}

			return `${formatValue(fromVal, curr)} - ${formatValue(toVal, curr)}`
		}
		if (typeof fromVal === 'number') {
			return formatValue(fromVal, curr)
		}
		if (typeof toVal === 'number') {
			return formatValue(toVal, curr)
		}
		return undefined
	}

	const primary = currency ? formatRange(decodedPriceFrom, decodedPriceTo, currency) : undefined
	const secondary =
		currencySecondary && (fromSecondaryExists || toSecondaryExists)
			? formatRange(decodedPriceFromSecondary, decodedPriceToSecondary, currencySecondary)
			: undefined

	if (!primary && !secondary) {
		return null
	}

	if (primary && secondary && primary !== secondary) {
		return `${primary} / ${secondary}`
	}

	return primary || secondary || null
}

export const getSecondaryPrice = (args: {
	primaryValue: number | undefined
	primaryMaxValue: number | undefined
	secondaryMaxValue: number | undefined
}): number | null => {
	const { primaryValue, primaryMaxValue, secondaryMaxValue } = args

	if (!primaryValue || !primaryMaxValue || !secondaryMaxValue) {
		return null
	}

	const percentage = (primaryValue / primaryMaxValue) * 100
	const secondaryValue = (percentage * secondaryMaxValue) / 100

	return Math.round(secondaryValue * 100) / 100 // Round to two decimal places
}

export const parseTextValueToNumberValue = (formattedValue: string) => {
	if (!formattedValue) return NaN // Handle empty input

	// Remove any non-numeric characters, except for periods
	const numericString = formattedValue.replace(/[^0-9]/g, '')

	// Use parseFloat to convert the string to a floating-point number
	return parseFloat(numericString)
}

export const TIME_SLOTS_TRANSLATIONS = (): Record<RESERVATIONS_TIME_SLOTS, string> => {
	const intl = getIntl()

	return {
		[RESERVATIONS_TIME_SLOTS.ANY]: intl.formatMessage({ id: 'Anytime', defaultMessage: 'Anytime' }),
		[RESERVATIONS_TIME_SLOTS.MORNING]: intl.formatMessage({ id: 'Morning', defaultMessage: 'Morning' }),
		[RESERVATIONS_TIME_SLOTS.AFTERNOON]: intl.formatMessage({ id: 'Afternoon', defaultMessage: 'Afternoon' }),
		[RESERVATIONS_TIME_SLOTS.EVENING]: intl.formatMessage({ id: 'Evening', defaultMessage: 'Evening' })
	}
}

export const SALONS_FILTER_ITEMS_CONFIG = (priceMin: number | undefined, priceMax: number | undefined) => {
	const { formatMessage } = getIntl()

	return {
		[SALONS_FILTER_TYPE.PRICE]: {
			defaultValue: [priceMin, priceMax],
			title: formatMessage({ id: 'Price' }),
			getSelectedTitle: (args: {
				currencyCode: CurrencyCode
				minPrice: number | undefined
				maxPrice: number | undefined
				priceFrom: number | undefined
				priceTo: number | undefined
				maxPriceSecondary: number | undefined
				currencyCodeSecondary: CurrencyCode | undefined
			}) => {
				const { currencyCode, minPrice, maxPrice, priceFrom, priceTo, maxPriceSecondary, currencyCodeSecondary } = args

				const missingBoundaryRanges = minPrice === undefined || maxPrice === undefined
				const missingSelectedPrices = Number.isNaN(priceFrom) || Number.isNaN(priceTo)
				const notChangedRanges = minPrice === priceFrom && maxPrice === priceTo

				if (missingBoundaryRanges || notChangedRanges || missingSelectedPrices || !currencyCode) {
					return null
				}

				if (minPrice === priceFrom && maxPrice !== priceTo) {
					let priceToFormatted = formatPrice({ from: priceTo, currency: currencyCode })

					if (typeof maxPriceSecondary === 'number' && currencyCodeSecondary) {
						const secondaryPriceTo = getSecondaryPrice({ primaryValue: priceTo, primaryMaxValue: maxPrice, secondaryMaxValue: maxPriceSecondary })
						const secondaryPriceToFormatted = formatPrice({
							from: secondaryPriceTo,
							currency: currencyCodeSecondary
						})
						priceToFormatted = priceToFormatted ? `${priceToFormatted} / ${secondaryPriceToFormatted}` : secondaryPriceToFormatted
					}

					return formatMessage({ id: 'to { priceTo }', defaultMessage: 'to { priceTo }' }, { priceTo: priceToFormatted })
				}

				if (maxPrice === priceTo && minPrice !== priceFrom) {
					let priceFromFormatted = formatPrice({ from: priceFrom, currency: currencyCode })

					if (typeof maxPriceSecondary === 'number' && currencyCodeSecondary) {
						const secondaryPriceFrom = getSecondaryPrice({
							primaryValue: priceFrom,
							primaryMaxValue: maxPrice,
							secondaryMaxValue: maxPriceSecondary
						})
						const secondaryPriceFromFormatted = formatPrice({
							from: secondaryPriceFrom,
							currency: currencyCodeSecondary
						})
						priceFromFormatted = priceFromFormatted ? `${priceFromFormatted} / ${secondaryPriceFromFormatted}` : secondaryPriceFromFormatted
					}

					return formatMessage({ id: 'from { priceFrom }', defaultMessage: 'from { priceFrom }' }, { priceFrom: priceFromFormatted })
				}

				const priceFromSecondary = getSecondaryPrice({ primaryValue: priceFrom, primaryMaxValue: maxPrice, secondaryMaxValue: maxPriceSecondary })
				const priceToSecondary = getSecondaryPrice({ primaryValue: priceTo, primaryMaxValue: maxPrice, secondaryMaxValue: maxPriceSecondary })

				return formatPrice({
					from: priceFrom,
					to: priceTo,
					fromSecondary: priceFromSecondary,
					toSecondary: priceToSecondary,
					currency: currencyCode,
					currencySecondary: currencyCodeSecondary
				})
			}
		},
		[SALONS_FILTER_TYPE.AVAILABLE_RS]: {
			defaultValue: false,
			title: formatMessage({ id: 'Available for online reservations', defaultMessage: 'Available for online reservations' }),
			getSelectedTitle: (available?: boolean) => {
				return available ? formatMessage({ id: 'Available for online reservations', defaultMessage: 'Available for online reservations' }) : null
			}
		},
		// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
		// [SALONS_FILTER_TYPE.HAS_GIFT]: {
		// 	defaultValue: false,
		// 	title: formatMessage({ id: 'Gift', defaultMessage: 'Gift' }),
		// 	getSelectedTitle: (hasGift?: boolean) => {
		// 		return hasGift ? formatMessage({ id: 'Gift', defaultMessage: 'Gift' }) : null
		// 	}
		// },
		[SALONS_FILTER_TYPE.OPENING_HOURS]: {
			defaultValue: false,
			title: formatMessage({ id: 'Is open', defaultMessage: 'Is open' }),
			getSelectedTitle: (open?: boolean) => {
				return open ? formatMessage({ id: 'Is open', defaultMessage: 'Is open' }) : null
			}
		},
		[SALONS_FILTER_TYPE.RATING]: {
			defaultValue: [],
			title: formatMessage({ id: 'Rating', defaultMessage: 'Rating' }),
			getSelectedTitle: (selectedRatings?: number[]) => {
				if (!selectedRatings?.length) {
					return null
				}
				const formattedRatings = selectedRatings.map((rating) => `${rating}*`)
				return `${formatMessage({ id: 'Rating', defaultMessage: 'Rating' })} ${formattedRatings.join(', ')}`
			}
		},
		[SALONS_FILTER_TYPE.COSMETICS]: {
			defaultValue: [],
			title: formatMessage({ id: 'Cosmetics they use', defaultMessage: 'Cosmetics they use' }),
			getSelectedTitle: (
				cosmetics: Cosmetic[],
				selectedCosmetics: string[]
			): {
				label: string | null
				count: number
			} => {
				if (selectedCosmetics.length === 0) {
					return { label: null, count: 0 }
				}

				const defaultLabel = formatMessage({ id: 'Cosmetics', defaultMessage: 'Cosmetics' })

				if (selectedCosmetics.length === 1) {
					return { label: cosmetics.find((cosmetic) => cosmetic.id === selectedCosmetics[0])?.name || defaultLabel, count: 0 }
				}

				const validCosmetics = selectedCosmetics.filter((selectedCosmeticId) => cosmetics.find((cosmetic) => cosmetic.id === selectedCosmeticId))

				return {
					label: defaultLabel,
					count: validCosmetics.length
				}
			}
		},
		[SALONS_FILTER_TYPE.DATE]: {
			defaultValue: {
				avResTimeSlotDayPart: RESERVATIONS_TIME_SLOTS.ANY,
				avResTimeSlotDateFrom: undefined,
				avResTimeSlotDateTo: undefined
			},
			title: formatMessage({ id: 'Date', defaultMessage: 'Date' }),
			getSelectedTitle: (avResTimeSlotDayPart: RESERVATIONS_TIME_SLOTS, avResTimeSlotDateFrom?: Dayjs, avResTimeSlotDateTo?: Dayjs) => {
				const timeSlotTranslation = avResTimeSlotDayPart !== RESERVATIONS_TIME_SLOTS.ANY ? TIME_SLOTS_TRANSLATIONS()[avResTimeSlotDayPart] || '' : ''

				if (!avResTimeSlotDateFrom && !avResTimeSlotDateTo) {
					return timeSlotTranslation
				}

				const formattedPeriod = timeSlotTranslation ? `, ${timeSlotTranslation}` : ''

				let formatFrom = DAY_NAME_DATE_FORMAT

				if (avResTimeSlotDateFrom && !avResTimeSlotDateTo) {
					if (avResTimeSlotDateFrom.isSame(dayjs(), 'date')) {
						return `${formatMessage({ id: 'Today', defaultMessage: 'Today' })}${formattedPeriod}`
					}

					if (avResTimeSlotDateFrom.year() !== dayjs().year()) {
						formatFrom = DAY_NAME_DATE_YEAR_FORMAT
					}
					return `${dayjs(avResTimeSlotDateFrom).format(formatFrom)}${formattedPeriod}`
				}

				if (avResTimeSlotDateTo) {
					let formatTo = DAY_NAME_DATE_FORMAT

					if (avResTimeSlotDateTo.year() !== dayjs().year()) {
						formatTo = DAY_NAME_DATE_YEAR_FORMAT
					}

					return `${dayjs(avResTimeSlotDateFrom).format(formatFrom)} - ${dayjs(avResTimeSlotDateTo).format(formatTo)}${formattedPeriod}`
				}
				return ''
			}
		},
		[SALONS_FILTER_TYPE.LANGUAGES]: {
			defaultValue: [],
			title: formatMessage({ id: 'Languages they speak', defaultMessage: 'Languages they speak' }),
			getSelectedTitle: (
				languages: Language[],
				selectedLanguages: string[]
			): {
				label: string | null
				count: number
			} => {
				if (selectedLanguages.length === 0) {
					return { label: null, count: 0 }
				}

				const defaultLabel = formatMessage({ id: 'Language', defaultMessage: 'Language' })

				if (selectedLanguages.length === 1) {
					return { label: languages.find((lang) => lang.id === selectedLanguages[0])?.name || defaultLabel, count: 1 }
				}

				const validLanguages = selectedLanguages.filter((selectedLanguageId) => languages.find((language) => language.id === selectedLanguageId))

				return { label: defaultLabel, count: validLanguages.length }
			}
		},
		[SALONS_FILTER_TYPE.SORTING]: {
			defaultValue: SORTING_OPTION.RECOMMENDED,
			title: formatMessage({ id: 'Sorting', defaultMessage: 'Sorting' }),
			getSelectedTitle: (sortingOptions: IDropdownOption[], currentOption: SORTING_OPTION, defaultOption = SORTING_OPTION.RECOMMENDED) => {
				if (currentOption === defaultOption) {
					return null
				}

				return (
					sortingOptions.find((option) => {
						return option.id === currentOption
					})?.label || null
				)
			}
		}
	}
}

// compares for equality only values, order of the elements doesn't matter
export const areValuesInArrayEqual = <T,>(array1: T[], array2: T[]) => {
	if (array1.length !== array2.length) {
		return false
	}

	const sortedA = [...array1].sort()
	const sortedB = [...array2].sort()

	return sortedA.every((value, index) => value === sortedB[index])
}

export const isNil = <T,>(value: T): boolean => {
	return value === undefined || value === null
}

export const SCROLLBAR_OPTIONS: OverlayScrollbarsComponentProps['options'] = {
	scrollbars: {
		autoHide: 'move',
		autoHideDelay: 1000
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isEnumValue = <T extends { [k: string]: string | number }>(checkValue: any, enumObject: T): checkValue is T[keyof T] =>
	(typeof checkValue === 'string' || typeof checkValue === 'number') && Object.values(enumObject).includes(checkValue)

const getValidCommonSalonsPageQuery = (query: CommonSalonsPageContainerQuery): CommonSalonsPageContainerQuery => {
	let validAvResTimeSlotDate
	let validAvResTimeSlotDateFrom
	let validAvResTimeSlotDateTo
	let latMy
	let lonMy

	if (query.avResTimeSlotDateFrom && query.avResTimeSlotDateTo) {
		if (dayjs(query.avResTimeSlotDateTo).isAfter(dayjs(validAvResTimeSlotDateFrom))) {
			validAvResTimeSlotDateFrom = query.avResTimeSlotDateFrom
			validAvResTimeSlotDateTo = query.avResTimeSlotDateTo
		}
	} else if (query.avResTimeSlotDate) {
		validAvResTimeSlotDate = query.avResTimeSlotDate
	} else if (query.avResTimeSlotDateFrom) {
		validAvResTimeSlotDate = query.avResTimeSlotDateFrom
	}

	const validServiceTotalPriceFrom = query.serviceTotalPriceFrom !== undefined ? query.serviceTotalPriceFrom : undefined

	let validServiceTotalPriceTo

	if (query.serviceTotalPriceTo !== undefined) {
		if (validServiceTotalPriceFrom !== undefined && validServiceTotalPriceFrom < query.serviceTotalPriceTo) {
			validServiceTotalPriceTo = query.serviceTotalPriceTo
		} else if (validServiceTotalPriceFrom === undefined) {
			validServiceTotalPriceTo = query.serviceTotalPriceTo
		}
	}

	if (query.latMy && query.lonMy) {
		latMy = query.latMy
		lonMy = query.lonMy
	}

	return {
		...query,
		latMy,
		lonMy,
		serviceTotalPriceFrom: validServiceTotalPriceFrom,
		serviceTotalPriceTo: validServiceTotalPriceTo,
		avResTimeSlotDate: validAvResTimeSlotDate,
		avResTimeSlotDateFrom: validAvResTimeSlotDateFrom,
		avResTimeSlotDateTo: validAvResTimeSlotDateTo
	}
}

export const getValidSalonsListPageQuery = (query: SalonsListPageContainerQuery): SalonsListPageContainerQuery => {
	return {
		...getValidCommonSalonsPageQuery(query)
	}
}

export const getValidSalonsCategoryPageQuery = (query: SalonsCategoryPageContainerQuery): SalonsCategoryPageContainerQuery => {
	return {
		...query,
		...getValidCommonSalonsPageQuery(query)
	}
}

export const getValidHomePageQuery = (query: HomePageContainerQuery): HomePageContainerQuery => {
	let latMy
	let lonMy

	if (query.latMy && query.lonMy) {
		latMy = query.latMy
		lonMy = query.lonMy
	}

	return {
		...query,
		latMy,
		lonMy
	}
}

export const isTimeSlotAvailable = (timeSlotData: AvailableRsTimeSlotsResponse['dates'], date: Dayjs) => {
	return timeSlotData.find((data) => dayjs(data.date).isSame(date, 'date'))?.available
}

export const DATE_PICKER_INIT_DATE = dayjs()

/**
 * NOTE: edit of url slug according to SEO feedback:
 * "The URL does not end with a slash, it is not an error, but it is not consistent throughout the notino website.
 * A URL without a trailing slash should be redirected to a variant with a trailing slash, and the canonical and the link from the home page should be corrected accordingly."
 */
export const formatUrlSlug = (slug: string | null | undefined) => {
	if (!slug) {
		return ''
	}

	return slug.endsWith('/') ? slug : `${slug}/`
}

export const truncateString = (inputString: string | null | undefined, maxLength = 150): string => {
	if (!inputString) {
		return ''
	}

	if (inputString.length <= maxLength) {
		return inputString
	}

	const truncatedString = inputString.substring(0, maxLength - 3) // -3 to account for the three dots
	return `${truncatedString}...`
}

export const findSmallestIndexMatch = (first: string[], second: string[]): string | undefined => {
	let smallestIndex = Infinity
	let smallestValue: string | undefined

	first.forEach((value) => {
		const index = second.indexOf(value)
		if (index !== -1 && index < smallestIndex) {
			smallestIndex = index
			smallestValue = value
		}
	})

	return smallestValue
}

export const RESERVATIONS_TIME_SLOTS_START_TIME: Record<Exclude<RESERVATIONS_TIME_SLOTS, 'ANY'>, number> & Record<RESERVATIONS_TIME_SLOTS.ANY, undefined> = {
	[RESERVATIONS_TIME_SLOTS.ANY]: undefined,
	[RESERVATIONS_TIME_SLOTS.MORNING]: 0,
	[RESERVATIONS_TIME_SLOTS.AFTERNOON]: 12,
	[RESERVATIONS_TIME_SLOTS.EVENING]: 18
}

/**
 * Remove accent and transform to lower case
 * Usefull for searching on FE
 * @link https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript
 */
export const transformToLowerCaseWithoutAccent = (source?: string): string =>
	source
		? source
				.toLowerCase()
				.normalize('NFD')
				.replace(/\p{Diacritic}/gu, '')
		: ''

export const transformQueryForLink = <T extends object = {}>(query: T) => {
	return Object.fromEntries(
		Object.entries(query).map(([key, value]) => {
			if (typeof value === 'boolean') {
				return [key, value ? 1 : 0]
			}
			return [key, value]
		})
	) as T
}

// type NestedPathsKeys<T> = T extends object ? keyof T : never
// type PageLink = Record<NestedPathsKeys<PathsType[AvailableLanguages]>, Function>

export const PAGE_LINKS /*: Record<NestedPathsKeys<PathsType[AvailableLanguages]>, Function> */ = {
	'/salons': (locale: string, queryParams?: Partial<HomePageContainerQuery>) => {
		const search = queryParams ? qs.stringify(transformQueryForLink(queryParams), { arrayFormat: 'none', skipNull: true }) : ''

		const href = `${Paths[locale as keyof typeof Paths]['/salons']}${search ? `?${search}` : ''}`
		return href
	},
	'/salons/all': (locale: string, queryParams?: Partial<SalonsListPageContainerQuery>) => {
		const search = queryParams ? qs.stringify(transformQueryForLink(queryParams), { arrayFormat: 'none', skipNull: true }) : ''

		const href = `${Paths[locale as keyof typeof Paths]['/salons/all']}${search ? `?${search}` : ''}`
		return href
	},
	'/salons/all/:categorySlug': (locale: string, categorySlug: string, queryParams?: Partial<SalonsCategoryPageContainerQuery>) => {
		const search = queryParams ? qs.stringify(transformQueryForLink(queryParams), { arrayFormat: 'none', skipNull: true }) : ''

		const href = `${Paths[locale as keyof typeof Paths]['/salons/all']}${formatUrlSlug(categorySlug)}${search ? `?${search}` : ''}`
		return href
	},
	'/salons/all/:categorySlug/:citySlug': (
		locale: string,
		categorySlug: string,
		citySlug: string,
		queryParams?: Partial<SalonsCategoryPageContainerQuery>
	) => {
		const search = queryParams ? qs.stringify(transformQueryForLink(queryParams), { arrayFormat: 'none', skipNull: true }) : ''

		const href = `${Paths[locale as keyof typeof Paths]['/salons/all']}${formatUrlSlug(categorySlug)}${formatUrlSlug(citySlug)}${search ? `?${search}` : ''}`
		return href
	},
	'/salons/:salonSlug': (
		locale: string,
		slugName: string,
		dataForQuery?: {
			reservationID?: string
			t?: string
			currentCategoryID?: string
			avResTimeSlotDayPart?: RESERVATIONS_TIME_SLOTS
			avResTimeSlotDate?: string
		}
	) => {
		const { currentCategoryID, avResTimeSlotDayPart, avResTimeSlotDate, reservationID, t } = dataForQuery || {}

		const salonPageQueryParams: SalonPageQuery = {
			reservationID,
			t,
			fe_selectedIndustryID: currentCategoryID,
			fe_selectedAvResTimeSlotDayPart: avResTimeSlotDayPart,
			fe_selectedAvResTimeSlotDate: avResTimeSlotDate
		}

		const search = qs.stringify(salonPageQueryParams, { skipNull: true })

		const itemHref = `${Paths[locale as keyof typeof Paths]['/salons']}${formatUrlSlug(slugName)}`
		return search ? `${itemHref}?${search}` : itemHref
	},
	'/salons/my-reservations': (locale: string) => {
		const href = `${Paths[locale as keyof typeof Paths]['/salons/my-reservations']}`
		return href
	},
	'/salons/my-reservations/:reservationID': (locale: string, reservationID: string) => {
		const href = `${Paths[locale as keyof typeof Paths]['/salons/my-reservations']}${formatUrlSlug(reservationID)}`
		return href
	},
	'/salons/passes': (locale: string) => {
		const href = `${Paths[locale as keyof typeof Paths]['/salons/passes']}`
		return href
	},
	'/salons/passes/:passID': (locale: string, passID: string) => {
		const href = `${Paths[locale as keyof typeof Paths]['/salons/passes']}${formatUrlSlug(passID)}`
		return href
	},
	'/salons/auth-complete': (locale: string) => {
		const href = `${Paths[locale as keyof typeof Paths]['/salons/auth-complete']}`
		return href
	}
}

export const PARTIAL_RATINGS_TRANSLATIONS = (): Record<PartialRatingType, string> => {
	const intl = getIntl()

	return {
		[PARTIAL_RATING.SERVICE]: intl.formatMessage({ id: 'Services', defaultMessage: 'Services' }),
		[PARTIAL_RATING.PLACE]: intl.formatMessage({ id: 'Environment', defaultMessage: 'Environment' }),
		[PARTIAL_RATING.COMMUNICATION]: intl.formatMessage({ id: 'Communication', defaultMessage: 'Communication' }),
		[PARTIAL_RATING.GENERAL_IMPRESSION]: intl.formatMessage({ id: 'Overall Impression', defaultMessage: 'Overall Impression' })
	}
}

export const PARTIAL_RATING_OPTIONS = () => {
	const ratingTranslations = PARTIAL_RATINGS_TRANSLATIONS()

	return [
		{ value: PARTIAL_RATING.SERVICE, label: ratingTranslations[PARTIAL_RATING.SERVICE] },
		{ value: PARTIAL_RATING.PLACE, label: ratingTranslations[PARTIAL_RATING.PLACE] },
		{
			value: PARTIAL_RATING.COMMUNICATION,
			label: ratingTranslations[PARTIAL_RATING.COMMUNICATION]
		},
		{
			value: PARTIAL_RATING.GENERAL_IMPRESSION,
			label: ratingTranslations[PARTIAL_RATING.GENERAL_IMPRESSION]
		}
	]
}

export const BOOKING_FORM_Z_INDEXES = {
	BOOKING_FORM: 9,
	DROPDOWN: 10
}

export const getDateTime = (date: string, time: string) => {
	return dayjs(`${date} ${time}`, 'YYYY-MM-DD HH:mm')
}

export const getEmployeeName = (employee: { firstName?: string; lastName?: string; email?: string }) => {
	let employeeName = ''

	if (employee.firstName && employee.lastName) {
		employeeName = `${employee.firstName} ${employee.lastName}`
	} else if (employee.firstName) {
		employeeName = employee.firstName
	} else if (employee.lastName) {
		employeeName = employee.lastName
	} else if (employee.email) {
		employeeName = employee.email
	} else {
		const intl = getIntl()
		employeeName = intl.formatMessage({ id: 'Employee', defaultMessage: 'Employee' })
	}

	return employeeName
}

export const formatReservationDateRange = (dateFormatter: IntlFormatters['formatDate'], startDate: Date, endDate: Date) => {
	const sameDay = dayjs(startDate).isSame(endDate, 'date')

	const formatDate = (date: Date) =>
		dateFormatter(date, {
			day: 'numeric',
			month: 'long',
			year: 'numeric',
			hour: 'numeric',
			minute: 'numeric'
		})

	const formatTime = (date: Date) =>
		dateFormatter(date, {
			hour: 'numeric',
			minute: 'numeric'
		})

	if (sameDay) {
		return `${formatDate(startDate)} - ${formatTime(endDate)}`
	}
	return `${formatDate(startDate)} - ${formatDate(endDate)}`
}

export const RESERVATION_BOOKING_MODAL_DEFAULT_INIT_DATA: ReservationBookingModalInitData = {
	initIndustryID: null,
	initServiceID: null,
	initAvResTimeSlotDayPart: null,
	initServiceCategoryParameterID: null,
	parameterSelectionData: null,
	employeeAndDateSelectionData: null,
	bookingConfirmationData: null,
	reservationsSummaryData: null,
	employeeAndDateGroupSelectionData: null,
	initCurrentStep: null
}

/**
 * @param groupedServicesByCategory
 * @param serviceId
 * @returns true if salon services has multiple industries with the same service
 */
export const hasMultipleIndustriesSameService = (
	groupedServicesByCategory: NonNullable<SalonServicesResponse['groupedServicesByCategory']>,
	serviceId: string
): boolean => {
	const categoriesWithService: Set<string> = new Set()

	// Use some to short-circuit the loop when a duplicate is found
	groupedServicesByCategory.some((group) => {
		const firstLevelCategory = group.category

		// Check the second-level categories
		return firstLevelCategory?.children.some((secondLevelChild) => {
			const secondLevelCategory = secondLevelChild.category

			// Check the third-level categories
			return secondLevelCategory?.children.some((thirdLevelChild) => {
				const { service } = thirdLevelChild

				if (service && service.id === serviceId) {
					categoriesWithService.add(firstLevelCategory.id)
				}

				// If more than one first-level category contains the service, return true to stop iteration
				return categoriesWithService.size > 1
			})
		})
	})

	return categoriesWithService.size > 1
}

type Category = NonNullable<NonNullable<SalonServicesResponse['groupedServicesByCategory']>>[number]

export const findServiceCategoryById = (
	categoryList: Category[],
	serviceId: string
): { industryData: ServicesTopCategoryData | null; serviceCategoryData: ServiceCategoryData | null } => {
	let foundServiceCategory: ServiceCategoryData | null = null
	let foundIndustry: ServicesTopCategoryData | null = null

	const searchInCategories = (categories: Category[]): void => {
		categories.forEach((category) => {
			const currentCategory = category.category

			if (currentCategory?.children) {
				currentCategory.children.forEach((childCategoryWrapper) => {
					const childCategory = childCategoryWrapper.category
					if (childCategory?.children) {
						childCategory.children.forEach((subChild) => {
							if (subChild.service?.id === serviceId) {
								foundServiceCategory = subChild
								foundIndustry = currentCategory
							}
						})

						// Recursive search for deeper nested categories
						if (!foundServiceCategory && childCategoryWrapper.category) {
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							searchInCategories([{ category: childCategoryWrapper.category as any }])
						}
					}
				})
			}
		})
	}
	searchInCategories(categoryList)
	return { industryData: foundIndustry, serviceCategoryData: foundServiceCategory }
}

export const areReservationsAllowed = (config: ConfigResponse['allowReservations'], shopID: ShopId) => {
	return config[shopID] === true
}

const RATING_VALUES = [RATINGS.ONE, RATINGS.TWO, RATINGS.THREE, RATINGS.FOUR, RATINGS.FIVE] as const

export const EXACT_RATING_OPTIONS = (
	ratingCounts: GetSalonsFilterCountResponse['exactRating'] | undefined,
	selectedRating: number[]
): CheckboxGroupOption<number>[] => {
	return RATING_VALUES.reduce<CheckboxGroupOption<number>[]>((acc, ratingValue, index) => {
		// hide ratings with 0 count (except selected)
		// if ratingCounts is undefined => show all ratings, but without counts
		if (ratingCounts && !ratingCounts[ratingValue] && !selectedRating.includes(ratingValue)) {
			return acc
		}

		acc.push({
			value: ratingValue,
			label: (
				<Ratings foreground={'text.primary'} background={'icon.disabled'} ratingId={`${RATING_ID.EXACT_RATING_OPTION}-${index}`} rating={ratingValue} />
			),
			description: ratingCounts ? formatNumber(ratingCounts[ratingValue] || 0) : undefined
		})

		return acc
	}, [])
}

type NotifData = {
	type: NonNullable<IToastProps['type']>
	icon?: IToastProps['icon']
	message: React.ReactNode
}

const CustomToast = ({ closeToast, data }: ToastContentProps<NotifData>) => {
	return <Toast type={data.type} content={data.message as JSX.Element} icon={data.icon} onClose={() => closeToast()} closeIcon={true} />
}

export const showNotifications = (messages: ErrorMessage[]) => {
	if (messages.length > 0) {
		messages.forEach((message) => {
			let notifData: NotifData = {
				type: ToastModel.ToastType.Info,
				message: message.message
			}
			const type = message.type.toLowerCase()
			switch (type) {
				case MSG_TYPE.ERROR:
					notifData = { ...notifData, type: ToastModel.ToastType.Error, icon: 'IconSolidInfo' }
					break
				case MSG_TYPE.INFO:
				case MSG_TYPE.SUCCESS:
					notifData = { ...notifData, type: ToastModel.ToastType.Info, icon: 'IconSolidInfo' }
					break
				default:
					break
			}

			toast<NotifData>(CustomToast, {
				data: notifData,
				autoClose: 3000
			})
		})
	}
}

type ResponseNotification = {
	message?: string
	type?: string
	path?: string
}

const isResponseNotification = (value: unknown): value is ResponseNotification => typeof value === 'object' && value !== null

const getResponseNotifications = (responseData: unknown): ResponseNotification[] => {
	if (Array.isArray(responseData)) {
		return responseData.filter(isResponseNotification)
	}

	if (typeof responseData === 'object' && responseData !== null && 'messages' in responseData) {
		const { messages } = responseData
		return Array.isArray(messages) ? messages.filter(isResponseNotification) : []
	}

	return []
}

export const showResponseNotifications = (responseData: unknown, displayNotification?: boolean) => {
	if (!displayNotification) {
		return
	}

	const notificationMessages = getResponseNotifications(responseData).reduce<ErrorMessage[]>((acc, message) => {
		if (!message.message || typeof message.type !== 'string') {
			return acc
		}

		acc.push({
			type: message.type.toLowerCase() as MSG_TYPE,
			message: message.message,
			path: message.path
		})

		return acc
	}, [])

	showNotifications(notificationMessages)
}

export const showErrorNotifications = (error: unknown, options?: { force?: boolean }) => {
	if (axios.isAxiosError(error)) {
		const axiosError = error as Omit<AxiosError<BasicAxiosError>, 'config'> & { config: CustomConfig }

		const shouldShowNotifications = axiosError.config.displayNotification !== false || options?.force

		const showNotificationToast = (notificationMessages: ErrorMessage[]) => {
			if (shouldShowNotifications) {
				showNotifications(notificationMessages)
			}
		}

		if (axiosError.response?.status === 504 || axiosError.message === 'Network Error') {
			showNotificationToast([
				{
					type: MSG_TYPE.ERROR,
					message: ERROR_MESSAGES().SERVER_ERROR
				}
			])
		} else if (!axiosError.response?.data?.messages || axiosError.response?.data?.messages.length === 0) {
			// if BE does not send message set general error message
			showNotificationToast([{ type: MSG_TYPE.ERROR, message: ERROR_MESSAGES().GENERAL_ERROR }])
		} else if (axiosError.response?.status === 401) {
			showNotificationToast([
				{
					type: MSG_TYPE.ERROR,
					message: ERROR_MESSAGES().UNAUTHORIZED_MSG
				}
			])
		} else {
			axiosError.response?.data.messages.forEach((message) => {
				if (axiosError.config.formErrors) {
					const { setError, errorsMap } = axiosError.config.formErrors
					const formKey = message.path ? findErrorKeyValue(message.path, errorsMap) : undefined
					if (formKey) {
						setError(formKey, { message: message.message })
					} else {
						showNotificationToast([
							{
								type: MSG_TYPE.ERROR,
								message: message.message
							}
						])
					}
				} else {
					showNotificationToast([
						{
							type: MSG_TYPE.ERROR,
							message: message.message
						}
					])
				}
			})
		}
	} else {
		showNotifications([
			{
				type: MSG_TYPE.ERROR,
				message: ERROR_MESSAGES().GENERAL_ERROR
			}
		])
	}
}

export const parseLanguageAndRegionFromLocale = (locale: string) => {
	let language
	let region
	try {
		const intlLocale = new Intl.Locale(locale)
		language = intlLocale.language
		region = intlLocale.region
	} catch (e) {
		Sentry.captureException(e, { extra: { identifier: 'parseLanguageAndRegionFromLocale' } })
	}

	return { language, region }
}

/**
 * Maps BCP 47 locale tags (e.g. sk-SK, de-AT) to app locale keys used in AVAILABLE_LANGUAGES.
 * Unlike parseLanguageAndRegionFromLocale, preserves region-specific keys such as de-AT.
 */
export const resolveAppLocale = (locale: string): AvailableLanguages => {
	if (AVAILABLE_LANGUAGES.includes(locale as AvailableLanguages)) {
		return locale as AvailableLanguages
	}

	const { language, region } = parseLanguageAndRegionFromLocale(locale)

	if (language && region) {
		const localeWithRegion = `${language}-${region}` as AvailableLanguages
		if (AVAILABLE_LANGUAGES.includes(localeWithRegion)) {
			return localeWithRegion
		}
	}

	if (language && AVAILABLE_LANGUAGES.includes(language as AvailableLanguages)) {
		return language as AvailableLanguages
	}

	return DEFAULT_LOCALE
}

export const checkWebPSupport = () => {
	try {
		return typeof document === 'object' && document.createElement('canvas').toDataURL('image/webp').indexOf('data:image/webp') === 0
	} catch {
		return false
	}
}

export const isMobileDevice = () => {
	return typeof window !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

/**
 * Generates a human-readable string representing occupancy count or range based on provided values.
 *
 * This function takes minimum and maximum occupancy values along with a service identifier to compute
 * and return a localized string describing the occupancy count. It supports scenarios where:
 * - Both minimum and maximum counts are equal, returning a single value (e.g., "5 People").
 * - Both minimum and maximum counts are specified and differ, returning a range (e.g., "3 - 5 People").
 * - Only the minimum count is specified, returning just the minimum value.
 *
 * If neither minimum nor maximum values are provided, the function logs an error to Sentry for debugging and returns a default representation of zero people.
 *
 * @param {number | undefined} min - The minimum occupancy count.
 * @param {number | undefined} max - The maximum occupancy count.
 * @param {string} serviceID - The identifier of the related service, used for error tracking purposes.
 * @returns {string} A localized string indicating the occupancy count or range.
 */

export const renderOccupancyCount = (min: number | undefined, max: number | undefined, serviceID: string) => {
	const intl = getIntl()

	const formatPeople = (count: number) => intl.formatMessage({ id: 'People' }, { count })

	// if max and min exist and are equal, show min value
	if (typeof min === 'number' && typeof max === 'number' && min === max) {
		return formatPeople(max)
	}

	// If max and min exist and are different, show range
	if (typeof min === 'number' && typeof max === 'number') {
		return `${min} - ${formatPeople(max)}`
	}

	// If only min exists show min
	if (typeof min === 'number') {
		return formatPeople(min)
	}
	// If none of these conditions are truly, send an error to Sentry
	Sentry.captureException(new Error('Max reservations count is not defined'), {
		extra: { serviceID, identifier: 'renderOccupancyCount' }
	})
	return formatPeople(0)
}

/**
 * Formats distance from meters to kilometers with 1 decimal place
 * @returns Formatted distance string with km unit translation (e.g., 300 m, 1,5 km, "125.5 km")
 * @param distance
 */

export const formatDistance = (distance: number | undefined): string => {
	if (typeof distance !== 'number') {
		return ''
	}

	const intl = getIntl()

	const distanceInKm = distance / 1000

	if (distanceInKm < 1) {
		const distanceInMeters = Math.round(distanceInKm * 1000)
		return intl.formatMessage({ id: 'distanceInM' }, { distance: distanceInMeters })
	}

	return intl.formatMessage({ id: 'distanceInKm' }, { distance: intl.formatNumber(distanceInKm, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })
}

/**
 * Formats a date into a localized string with support for relative day labels
 * like "Today" and "Tomorrow". Falls back to standard date formatting
 * when the date is not relative to the current day.
 *
 * Uses `react-intl` under the hood for both message translation
 * and date formatting.
 *
 * @param dateInput - Date value as an ISO string (or any parsable date string) e.g.: '2025-03-14'
 * @param options - Optional Intl.DateTimeFormat options for customizing output format e.g.: { weekday: 'long' }
 *
 * @returns A localized string:
 * - "Today" if the date is today
 * - "Tomorrow" if the date is tomorrow
 * - otherwise a formatted date string (e.g. "Friday, March 14" or "Friday, March 14, 2025" when not in the current year)
 *
 * @example
 * formatRelativeDate('2025-03-14')
 * // → "Friday, March 14" (when 2025 is the current year)
 * // → "Friday, March 14, 2025" (when 2025 is not the current year)
 *
 * @example
 * formatRelativeDate(new Date().toISOString())
 * // → "Today"
 *
 * @example
 * formatRelativeDate('2025-03-14', { weekday: 'long' })
 * // → "Friday"
 *
 * @remarks
 * - Output is locale-aware (e.g. "pátek" vs "Friday")
 * - Capitalization follows language rules (not forced)
 * - Ensure correct timezone handling if working with UTC dates
 */

export const formatRelativeDate = (dateInput: string, options?: FormatDateOptions) => {
	const intl = getIntl()
	const date = dayjs(dateInput)
	const today = dayjs()

	if (date.isSame(today, 'day')) {
		return intl.formatMessage({
			id: 'Today',
			defaultMessage: 'Today'
		})
	}

	if (date.isSame(today.add(1, 'day'), 'day')) {
		return intl.formatMessage({
			id: 'Tomorrow',
			defaultMessage: 'Tomorrow'
		})
	}

	const dateOptions: FormatDateOptions = {
		...(options || {
			weekday: 'long',
			day: 'numeric',
			month: 'long'
		})
	}

	if (date.year() !== today.year()) {
		dateOptions.year = 'numeric'
	} else {
		delete dateOptions.year
	}

	return intl.formatDate(date.toDate(), dateOptions)
}

const REVIEW_RELATIVE_TIME_OPTS = { numeric: 'auto' as const }

/**
 * Salon review / response: within 7 calendar days ({@link Intl.RelativeTimeFormat} — seconds/minutes/hours same day, then days); otherwise medium date. Aligns with other app submodules.
 */
export const formatSalonReviewDate = (dateInput: string | number | Date, intl: Pick<IntlShape, 'formatDate' | 'formatRelativeTime'>): string => {
	const then = dayjs(dateInput)
	if (!then.isValid()) {
		return ''
	}
	const now = dayjs()
	const mediumDate = { dateStyle: 'medium' as const }

	if (then.isAfter(now)) {
		return intl.formatDate(then.toDate(), mediumDate)
	}

	const calendarDayDiff = now.startOf('day').diff(then.startOf('day'), 'day')
	if (calendarDayDiff >= 8) {
		return intl.formatDate(then.toDate(), mediumDate)
	}

	const msAgo = now.diff(then)
	const secondsAgo = Math.floor(msAgo / 1000)

	if (calendarDayDiff === 0) {
		if (secondsAgo < 60) {
			return intl.formatRelativeTime(-secondsAgo, 'second', REVIEW_RELATIVE_TIME_OPTS)
		}
		const minutesAgo = Math.floor(secondsAgo / 60)
		if (minutesAgo < 60) {
			return intl.formatRelativeTime(-minutesAgo, 'minute', REVIEW_RELATIVE_TIME_OPTS)
		}
		const hoursAgo = Math.floor(minutesAgo / 60)
		return intl.formatRelativeTime(-hoursAgo, 'hour', REVIEW_RELATIVE_TIME_OPTS)
	}

	return intl.formatRelativeTime(-calendarDayDiff, 'day', REVIEW_RELATIVE_TIME_OPTS)
}

/** Relative paths are internal. Absolute http(s) or // URLs are internal when host matches the shop URL for the environment and locale. */
export const isExternalLink = (link: string, productionEnvironment: PRODUCTION_ENVIRONMENT, locale: string): boolean => {
	const trimmed = link.trim()
	if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://') && !trimmed.startsWith('//')) {
		return false
	}
	let linkHost: string
	try {
		const absolute = trimmed.startsWith('//') ? `https:${trimmed}` : trimmed
		linkHost = new URL(absolute).hostname
	} catch {
		return true
	}
	const shop = getEnvironmentAndLocaleSpecificShopsConfig(productionEnvironment, locale)
	try {
		return new URL(shop.url).hostname !== linkHost
	} catch {
		return true
	}
}

/** Returns the rel and target attributes for a link based on whether it is an external link. */
export const getLinkAttributes = (
	link: string,
	productionEnvironment: PRODUCTION_ENVIRONMENT,
	locale: string
): { rel: string | undefined; target: string | undefined } => {
	if (isExternalLink(link, productionEnvironment, locale)) {
		return { rel: 'nofollow noopener noreferrer', target: '_blank' }
	}
	return { rel: undefined, target: undefined }
}

/** True when the page is viewed on a shop domain that does not match the salon's country. */
export const isSalonNonHomeShopView = (currentShopCountryCode: CountryCode, salonCountryCode: string | undefined): boolean =>
	Boolean(salonCountryCode && currentShopCountryCode !== salonCountryCode)
